/**
 * © 2024 Little Shilling, Inc.
 * Shon Little
 * Created: 2024-07-25
 * Updated: 2024-06-19
 */

// Add third-party dependencies.
import { useState } from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import {
  FormControl,
  TextField,
  InputAdornment,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Password requirements component.
 * @param {Object} props - Component props.
 * @param {boolean} props.isVisible - Whether the component should be visible.
 * @param {Object} props.checks - Password validation checks.
 * @returns {React.ReactElement|null} component.
 */
const PasswordRequirements = ({ isVisible, checks }) => {
  if (!isVisible) {
    return null;
  }

  return (
    <Box sx={{ mt: 1, mb: 1, fontSize: '0.75rem' }}>
      <Typography variant="caption" color="textSecondary">
        Password requirements:
      </Typography>
      <List dense disablePadding sx={{ mt: 0.5 }}>
        <ListItem dense disablePadding sx={{ py: 0.25 }}>
          <ListItemIcon sx={{ minWidth: 28 }}>
            {checks.length ? (
              <CheckIcon fontSize="small" color="success" />
            ) : (
              <CloseIcon fontSize="small" color="error" />
            )}
          </ListItemIcon>
          <ListItemText primary="At least 8 characters" primaryTypographyProps={{ variant: 'caption' }} />
        </ListItem>
        <ListItem dense disablePadding sx={{ py: 0.25 }}>
          <ListItemIcon sx={{ minWidth: 28 }}>
            {checks.uppercase ? (
              <CheckIcon fontSize="small" color="success" />
            ) : (
              <CloseIcon fontSize="small" color="error" />
            )}
          </ListItemIcon>
          <ListItemText primary="At least one uppercase letter" primaryTypographyProps={{ variant: 'caption' }} />
        </ListItem>
        <ListItem dense disablePadding sx={{ py: 0.25 }}>
          <ListItemIcon sx={{ minWidth: 28 }}>
            {checks.lowercase ? (
              <CheckIcon fontSize="small" color="success" />
            ) : (
              <CloseIcon fontSize="small" color="error" />
            )}
          </ListItemIcon>
          <ListItemText primary="At least one lowercase letter" primaryTypographyProps={{ variant: 'caption' }} />
        </ListItem>
        <ListItem dense disablePadding sx={{ py: 0.25 }}>
          <ListItemIcon sx={{ minWidth: 28 }}>
            {checks.number ? (
              <CheckIcon fontSize="small" color="success" />
            ) : (
              <CloseIcon fontSize="small" color="error" />
            )}
          </ListItemIcon>
          <ListItemText primary="At least one number" primaryTypographyProps={{ variant: 'caption' }} />
        </ListItem>
      </List>
    </Box>
  );
};

PasswordRequirements.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  checks: PropTypes.shape({
    length: PropTypes.bool.isRequired,
    uppercase: PropTypes.bool.isRequired,
    lowercase: PropTypes.bool.isRequired,
    number: PropTypes.bool.isRequired,
  }).isRequired,
};

/**
 * FormText component with enhanced validation feedback.
 * @example
 * return (
 *   <FormText
 *     control={control}
 *     name="password"
 *     label="Password"
 *     type="password"
 *     showValidationIcon={true}
 *     showPasswordRequirements={true}
 *   />
 * )
 * @returns {React.ReactElement} component.
 */
const FormText = ({
  control,
  name,
  label,
  defaultValue,
  rules,
  required,
  type,
  autoFocus,
  onKeyDown,
  margin,
  inputProps,
  shrinkLabel,
  showValidationIcon,
  showPasswordRequirements,
  helperText,
}) => {
  // State for tracking field interaction
  const [isFocused, setIsFocused] = useState(false);
  const [isTouched, setIsTouched] = useState(false);
  const [fieldValue, setFieldValue] = useState(defaultValue || '');

  // Determine if this is a password field
  const isPasswordField = type === 'password' || name.includes('password');

  // Password validation checks
  const passwordChecks =
    isPasswordField && showPasswordRequirements
      ? {
          length: fieldValue.length >= 8,
          uppercase: /[A-Z]/.test(fieldValue),
          lowercase: /[a-z]/.test(fieldValue),
          number: /\d/.test(fieldValue),
        }
      : {
          length: false,
          uppercase: false,
          lowercase: false,
          number: false,
        };

  // Handle field focus
  const handleFocus = (event, onFocus) => {
    setIsFocused(true);
    if (onFocus) onFocus(event);
  };

  // Handle field blur
  const handleBlur = (event, onBlur) => {
    setIsFocused(false);
    setIsTouched(true);
    if (onBlur) onBlur(event);
  };

  // Handle field change
  const handleChange = (event, onChange) => {
    setFieldValue(event.target.value);
    if (onChange) onChange(event);
  };

  // Determine if password requirements should be visible
  const showPasswordReqs = isPasswordField && showPasswordRequirements && (isFocused || isTouched);

  // Render component.
  return (
    <FormControl fullWidth margin={margin}>
      <Controller
        control={control}
        name={name}
        defaultValue={defaultValue}
        rules={rules}
        render={({ field, fieldState }) => {
          // Destructure field and fieldState objects
          const { value, onChange, onBlur, onFocus } = field;
          const { isDirty, error } = fieldState;

          // Determine if we should show validation state
          const showValidation = isTouched && isDirty;
          const isValid = isDirty && !error;

          // Create endAdornment with validation icon if needed
          const { endAdornment: originalEndAdornment } = inputProps;
          let endAdornment = originalEndAdornment;
          if (showValidationIcon && showValidation) {
            endAdornment = (
              <>
                {originalEndAdornment}
                <InputAdornment position="end">
                  {isValid ? <CheckCircleOutlineIcon color="success" /> : error && <ErrorOutlineIcon color="error" />}
                </InputAdornment>
              </>
            );
          }

          return (
            <>
              <TextField
                value={value}
                onChange={(e) => handleChange(e, onChange)}
                onBlur={(e) => handleBlur(e, onBlur)}
                onFocus={(e) => handleFocus(e, onFocus)}
                name={name}
                id={name}
                autoComplete={name}
                label={label}
                required={required}
                fullWidth
                type={type}
                autoFocus={autoFocus}
                error={!!error}
                helperText={error ? error.message : helperText}
                onKeyDown={onKeyDown}
                InputProps={{
                  ...inputProps,
                  endAdornment,
                }}
                InputLabelProps={shrinkLabel ? { shrink: true } : {}}
              />
              <PasswordRequirements isVisible={showPasswordReqs} checks={passwordChecks} />
            </>
          );
        }}
      />
    </FormControl>
  );
};

// Set component property types.
FormText.propTypes = {
  control: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  defaultValue: PropTypes.string,
  rules: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  required: PropTypes.bool,
  type: PropTypes.string,
  autoFocus: PropTypes.bool,
  onKeyDown: PropTypes.func,
  margin: PropTypes.string,
  inputProps: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  shrinkLabel: PropTypes.bool,
  showValidationIcon: PropTypes.bool,
  showPasswordRequirements: PropTypes.bool,
  helperText: PropTypes.string,
};

// Set component default properties.
FormText.defaultProps = {
  label: '',
  defaultValue: '',
  rules: { required: 'Field is required.' },
  required: true,
  type: 'text',
  autoFocus: false,
  onKeyDown: null,
  margin: 'normal',
  inputProps: {},
  shrinkLabel: false,
  showValidationIcon: false,
  showPasswordRequirements: false,
  helperText: '',
};

// Export component.
export default FormText;
